import { useCallback, useState } from 'react';

import { getError, referralsService } from '@aslanpay/portal-common-lib';

import { formatReferralCode, isReferralCodeComplete } from 'app/utils/referralCode';

const INVALID_MESSAGE = 'This referral code is not valid.';
const RATE_LIMITED_MESSAGE = 'Too many attempts. Please try again later.';

interface UseReferralCodeResult {
  referralCode: string;
  error: string;
  isValidating: boolean;
  /** Formats the raw input and clears any previous error. */
  handleChange: (value: string) => void;
  /**
   * Validates the current code against the backend. An empty code is always valid. Resolves to the
   * code to send with the sign-up request (undefined when empty) or throws nothing — on failure it
   * sets the inline error and resolves to `false`.
   */
  validate: () => Promise<string | undefined | false>;
  reset: () => void;
}

/**
 * Owns the optional referral-code field shown to employees during sign-up: mobile-style formatting,
 * backend validation, and the inline error that blocks the step until the code is fixed or cleared.
 */
const useReferralCode = (): UseReferralCodeResult => {
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const handleChange = useCallback((value: string) => {
    setError('');
    setReferralCode(formatReferralCode(value));
  }, []);

  const reset = useCallback(() => {
    setReferralCode('');
    setError('');
    setIsValidating(false);
  }, []);

  const validate = useCallback(async () => {
    if (referralCode === '') {
      setError('');
      return undefined;
    }

    if (!isReferralCodeComplete(referralCode)) {
      setError(INVALID_MESSAGE);
      return false;
    }

    setIsValidating(true);
    try {
      await referralsService.validateReferralCode({ referralCode });
      setError('');
      return referralCode;
    } catch (caught) {
      setError(getError(caught as Error).status === 429 ? RATE_LIMITED_MESSAGE : INVALID_MESSAGE);
      return false;
    } finally {
      setIsValidating(false);
    }
  }, [referralCode]);

  return { referralCode, error, isValidating, handleChange, validate, reset };
};

export default useReferralCode;
